// Unit 13 — Troubleshooting Methodology (Troubleshooting Lab with an evidence-backed investigation log)
import { h } from '../../ui/h.js';
import { note, readConfirm } from '../../ui/widgets.js';
import { branch, branchLayout } from './topologies.js';
import { ran, pinged, resolved, webWorks, hostCfg } from './helpers.js';
import { renew } from '../../engine/pc/winshell.js';
import { inSubnet } from '../../engine/net/addr.js';

export const LAYERS = ['Physical', 'Endpoint', 'Switching / VLAN', 'Default gateway', 'Routing', 'DHCP', 'DNS', 'NAT', 'Internet / service'];

function network() {
  const n = branch({ vlan: 10, vlanName: 'USERS' });
  const R1 = n.devices.R1;
  R1.dhcp.pools.USERS = { name: 'USERS', network: '192.168.10.0', mask: '255.255.255.0', router: '192.168.10.1', dns: ['8.8.8.8'] };
  R1.dhcp.excluded.push(['192.168.10.1', '192.168.10.99']);
  const PC2 = n.devices.PC2;
  PC2.ipcfg = { dhcp: true, ip: null, mask: null, gw: null, dns: [], ipv6: [] };
  n.devices.SW1.interfaces['FastEthernet0/2'].accessVlan = 99; // the fault
  n.devices.SW1.vlans[99] = { name: 'VLAN0099' };
  renew(n, PC2); // boots → DHCP fails → APIPA
  return n;
}
const layout = {
  ...branchLayout,
  nodes: { ...branchLayout.nodes, PC2: { ...branchLayout.nodes.PC2, sub: 'DHCP client · desk 15' }, PC1: { ...branchLayout.nodes.PC1, sub: 'DHCP client · desk 14' } },
  zones: [{ ...branchLayout.zones[0], label: 'BRANCH-01 · VLAN 10 USERS · 192.168.10.0/24 · DHCP on R1' }, branchLayout.zones[1]],
};

/** Evidence-backed investigation log. Each row must cite a command actually run. */
function investigationLog() {
  const RULES = {
    Physical: { need: 'OK', dev: /^(SW1|PC2)$/, keys: ['show interfaces status', 'show interfaces <IF>', 'ipconfig', 'ipconfig /all', 'show ip interface brief'] },
    Endpoint: { need: 'FAIL', dev: /^PC2$/, keys: ['ipconfig', 'ipconfig /all', 'ipconfig /renew'] },
    'Switching / VLAN': { need: 'FAIL', dev: /^SW1$/, keys: ['show vlan brief', 'show vlan', 'show interfaces status', 'show running-config', 'show running-config interface <IF>', 'show mac address-table', 'show mac address-table dynamic', 'show interfaces trunk'] },
  };
  /** Commands the learner can cite (successful, de-duplicated, the latest 40). */
  const citable = (rt) => {
    const cmds = rt.state.log.filter((e) => e.type === 'cmd' && e.key && !e.empty && e.ok !== false).map((e, i) => ({ i, label: `${e.device}: ${e.line}`, e }));
    return [...new Map(cmds.map((c) => [c.label, c])).values()].slice(-40);
  };
  /** Review the log → { layer|theory: message } (empty when accepted). */
  const review = (s, rt) => {
    const m = {};
    const uniq = citable(rt);
    const rows = s?.rows && typeof s.rows === 'object' ? s.rows : {};
    for (const [L, rule] of Object.entries(RULES)) {
      const r = rows[L] || {};
      const c = uniq.find((x) => x.label === r.ev);
      if (!c) m[L] = 'Cite a command you actually ran for this layer.';
      else if (!rule.dev.test(c.e.device) || !rule.keys.includes(c.e.key)) m[L] = 'That command does not test this layer on the right device.';
      else if (r.res !== rule.need) m[L] = 'Compare the result you recorded with what that output actually shows.';
      else if (String(r.obs || '').trim().length < 8) m[L] = 'Write what you observed in the output.';
    }
    const th = s?.theory || {};
    if (String(th.text || '').trim().length < 25) m.theory = 'Write your theory in a full sentence.';
    else if (th.layer !== 'Switching / VLAN') m.theory = 'Does that layer explain every piece of evidence in your log? Look again at the first real failure.';
    return m;
  };
  return {
    id: 'u13-log', blocker: 'Complete the investigation log and theory',
    verify: (d, rt) => Object.keys(review(d, rt)).length === 0,
    mount(el, rt, api) {
      const s = api.state.data;
      s.rows = s.rows || {};
      const render = (msgs) => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, 'INVESTIGATION LOG · bottom-up', api.state.done ? h('span', { class: 'ok' }, '✓ THEORY ACCEPTED') : null));
        el.append(h('p', { class: 'small muted' }, 'For each layer you test: cite the command you ran, record the result, and write what you observed. Work up from Physical until you find the first failure that explains the symptom.'));
        const uniq = citable(rt);
        const g = h('div', { class: 'findings' });
        for (const L of ['Physical', 'Endpoint', 'Switching / VLAN', 'Default gateway', 'DHCP']) {
          const r = (s.rows[L] = s.rows[L] || { ev: '', res: '', obs: '' });
          g.append(h('div', { class: 'finding', 'data-layer': L },
            h('div', { class: 'full' }, h('b', {}, L)),
            h('label', { class: 'fld' }, h('span', {}, 'Evidence (command you ran)'),
              h('select', { disabled: api.state.done || null, 'data-testid': `log-ev-${L}`, onchange: (e) => { r.ev = e.target.value; api.save(); } }, h('option', { value: '' }, '— not tested —'), uniq.map((c) => h('option', { value: c.label, selected: r.ev === c.label || null }, c.label)))),
            h('label', { class: 'fld' }, h('span', {}, 'Result'),
              h('select', { disabled: api.state.done || null, 'data-testid': `log-res-${L}`, onchange: (e) => { r.res = e.target.value; api.save(); } }, ['', 'OK', 'FAIL'].map((v) => h('option', { value: v, selected: r.res === v || null }, v || '—')))),
            h('label', { class: 'fld full' }, h('span', {}, 'Observed'), h('input', { value: r.obs, disabled: api.state.done || null, 'data-testid': `log-obs-${L}`, oninput: (e) => { r.obs = e.target.value; } })),
            msgs?.[L] ? h('div', { class: 'w-fb bad full' }, msgs[L]) : null));
        }
        el.append(g);
        s.theory = s.theory || { layer: '', text: '' };
        el.append(h('div', { class: 'finding' },
          h('div', { class: 'full' }, h('b', {}, 'THEORY')),
          h('label', { class: 'fld' }, h('span', {}, 'Root-cause layer'), h('select', { disabled: api.state.done || null, 'data-testid': 'log-theory-layer', onchange: (e) => { s.theory.layer = e.target.value; } }, h('option', { value: '' }, '—'), LAYERS.map((l) => h('option', { value: l, selected: s.theory.layer === l || null }, l)))),
          h('label', { class: 'fld full' }, h('span', {}, 'Theory: what is wrong and why it explains the symptom'), h('textarea', { disabled: api.state.done || null, 'data-testid': 'log-theory-text', oninput: (e) => { s.theory.text = e.target.value; } }, s.theory.text)),
          msgs?.theory ? h('div', { class: 'w-fb bad full' }, msgs.theory) : null));
        if (!api.state.done) el.append(h('div', { class: 'row' }, h('button', { class: 'btn small', 'data-testid': 'log-check', onclick: () => {
          const m = review(s, rt);
          const ok = !Object.keys(m).length;
          rt.action('log-check', { ok });
          if (ok) api.done();
          api.save();
          render(m);
        } }, 'Submit theory for review')));
      };
      render();
      rt.on((t) => { if (t === 'event' && !api.state.done && el.isConnected) render(); });
    },
  };
}

export default {
  id: 'u13', num: '13', title: 'Troubleshooting Methodology', type: 'Troubleshooting Lab', implemented: true,
  objective: 'Apply a structured, bottom-up method: define the problem, gather evidence layer by layer, form a theory, test one change, verify, document.',
  network, layout, adapterGui: false,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'A method beats a memory', workspace: false,
      content: `
<pre>1 DEFINE the problem       who, what, since when, what changed?
2 GATHER evidence          commands, logs, the user's own words
3 ANALYSE                  expected vs observed, layer by layer
4 ELIMINATE                rule layers out with proof, not hunches
5 THEORISE                 the first failure that explains ALL the evidence
6 TEST                     one change at a time, with a rollback plan
7 VERIFY                   from the user's point of view
8 DOCUMENT                 root cause, fix, prevention</pre>
<h3>The bottom-up checklist for “no network”</h3>
<table><tr><th>#</th><th>Layer</th><th>Question</th><th>Typical evidence</th></tr>
<tr><td>1</td><td>Physical</td><td>Link up? Right cable, right port?</td><td><code>show interfaces status</code>, Media State</td></tr>
<tr><td>2</td><td>Endpoint</td><td>Valid IP, mask, gateway, DNS?</td><td><code>ipconfig /all</code></td></tr>
<tr><td>3</td><td>Switching / VLAN</td><td>Port in the right VLAN? MAC learned?</td><td><code>show vlan brief</code>, <code>show mac address-table</code></td></tr>
<tr><td>4</td><td>Default gateway</td><td>Can the host reach it?</td><td><code>ping</code>, <code>arp -a</code></td></tr>
<tr><td>5</td><td>Routing</td><td>Does the router have a route?</td><td><code>show ip route</code>, <code>tracert</code></td></tr>
<tr><td>6–7</td><td>DHCP · DNS</td><td>Lease obtained? Names resolve?</td><td><code>ipconfig /renew</code>, <code>nslookup</code></td></tr>
<tr><td>8–9</td><td>NAT · Internet</td><td>Translations built? Service up?</td><td><code>show ip nat translations</code>, <code>curl</code></td></tr></table>
<div class="callout">A <b>169.254.x.x</b> address is a symptom. Ask <i>why</i> the DHCP server could not be reached — the answer is usually lower in the stack.</div>`,
      widgets: [readConfirm({ id: 'u13-read' })],
      question: 'Why is changing two things at once a bad idea, even if the problem goes away?',
    },
    {
      id: 'investigate', phase: 'INVESTIGATE → THEORY', title: 'Desk 15 has “no internet”', devices: ['PC2', 'SW1', 'R1'],
      ticket: { id: '1013', priority: 3, from: 'Mark (Desk 15)', subject: 'No internet since the desk move', body: 'Facilities moved my desk yesterday. The PC says “No Internet access”. Everyone else in the row is fine.' },
      content: '<p>Work bottom-up. Collect evidence at each layer on the right device, then fill in the investigation log and submit your theory. <b>Do not fix anything yet</b> — a good theory comes before the change.</p>',
      evidence: [
        { id: 'ipc', text: 'PC2: IP configuration captured', match: ran('PC2', ['ipconfig', 'ipconfig /all']) },
        { id: 'renew', text: 'PC2: DHCP renewal attempted', match: ran('PC2', 'ipconfig /renew') },
        { id: 'link', text: 'SW1: port status checked', match: ran('SW1', 'show interfaces status') },
        { id: 'vlan', text: 'SW1: VLAN membership checked', match: ran('SW1', ['show vlan brief', 'show vlan', 'show running-config', 'show running-config interface <IF>']) },
      ],
      widgets: [investigationLog()],
      question: 'The PC has a 169.254 address. Which layer does that point at — and which layer might actually <i>cause</i> it?',
      hints: ['Start with <code>ipconfig /all</code> on PC2 and <code>show interfaces status</code> on SW1. Is the link up? Which VLAN is Fa0/2 in?', 'Compare Fa0/2 with the ports of PCs that work (Fa0/1) and the router uplink (Fa0/24).', 'DHCP Discover is a broadcast. Broadcasts never leave their VLAN.'],
      verifyLabel: 'Theory accepted — continue to test',
    },
    {
      id: 'fix', phase: 'TEST → FIX', title: 'Test your theory with one change', devices: ['PC2', 'SW1', 'R1'],
      content: '<p>Make <b>one</b> change that tests your theory. Then make PC2 request a new address.</p>',
      tasks: [
        { id: 'vlan', text: 'Desk 15 port is in the users VLAN', check: (rt) => rt.net.devices.SW1.interfaces['FastEthernet0/2'].accessVlan === 10, fail: 'Fa0/2 is still in the wrong VLAN.' },
        { id: 'lease', text: 'PC2 holds a DHCP lease in 192.168.10.0/24', check: (rt) => { const c = hostCfg(rt, 'PC2'); return !!rt.net.devices.PC2.lease && inSubnet(c?.ip, '192.168.10.0', 24); }, fail: 'PC2 has no valid lease yet.' },
        { id: 'others', text: 'Other users unaffected (PC1 still browses)', check: (rt) => webWorks(rt, 'PC1'), fail: 'Your change broke another user.' },
      ],
      hints: ['<code>interface fa0/2</code> → <code>switchport access vlan 10</code>.', 'Windows will not notice on its own for a while: run <code>ipconfig /renew</code>.'],
    },
    {
      id: 'verify', phase: 'VERIFY → DOCUMENT', title: 'Verify from the user side and document', devices: ['PC2', 'SW1'],
      evidence: [
        { id: 'renew', text: 'PC2: DHCP renewal succeeds', match: (ev) => ran('PC2', 'ipconfig /renew')(ev) && /192\.168\.10\./.test(ev.output) },
        { id: 'inet', text: 'PC2: internet reachable by IP', match: pinged('PC2', '8.8.8.8', { success: true }) },
        { id: 'dns', text: 'PC2: names resolve', match: resolved('PC2', null) },
      ],
      widgets: [note({ id: 'u13-doc', title: 'INCIDENT RECORD', prompt: 'Root cause, the evidence that proved it, the single change you made, and how to prevent it (e.g. port labelling after desk moves).', min: 60 })],
      tasks: [{ id: 'web', text: 'PC2 can browse www.example.com', check: (rt) => webWorks(rt, 'PC2'), fail: 'PC2 cannot browse yet.' }],
      verifyLabel: 'Verify & close incident',
    },
  ],
  review: () => '<p>Physical was fine, the endpoint showed the symptom (APIPA), and the <b>first real failure</b> was one layer down from where it appeared: Fa0/2 in VLAN 99 kept the DHCP broadcast away from R1. One change, verified, documented.</p>',
};
